<script setup lang="ts">
    import IconList from "@/components/calendars/overlayPanels/IconList/IconList.vue";
    import IconListItem from "@/components/calendars/overlayPanels/IconList/IconListItem.vue";
    import KSection from "@/components/generic/KSection.vue";
    import { NllLockType } from "@/store/modules/recept/helpers";
    import { Handelse, ReceptforskrivningDisplay } from "@/tabs/Receptforskrivning/types";
    import moment from "moment";

    defineProps<{
        data: ReceptforskrivningDisplay;
    }>();

    const getDatetime = ({ tidpunkt }: { tidpunkt: string }) => {
        return moment.parseZone(tidpunkt).format("yyyy-MM-DD HH:mm").toString();
    };

    const getHandelse = ({ handelse }: { handelse: Handelse }) => {
        if (handelse.info) {
            if (handelse.handelse === "Registrera spärr") {
                return NllLockType[handelse.info as keyof typeof NllLockType].register_display;
            }
            if (handelse.handelse === "Avregistrera spärr") {
                return NllLockType[handelse.info as keyof typeof NllLockType].deregister_display;
            }
        }
        return handelse.handelse;
    };

    const ejDelges = " (får ej delges patient)";
</script>

<template>
    <div class="recept-tab-handelser">
        <KSection
            v-for="handelse in data?.handelser"
            :key="handelse.id"
            :name="handelse.id"
            :expanded="false"
            :numbered="false"
            variant="boxed"
            chevronPlacement="left"
        >
            <template #heading>
                <div class="heading">
                    <span class="handelse">{{ getHandelse({ handelse }) }}</span>
                    <span>{{ getDatetime({ tidpunkt: handelse.tidpunkt }) }}</span>
                </div>
            </template>
            <div class="section-content">
                <div class="left">
                    <IconList v-if="handelse.orsak || handelse.beskrivning">
                        <IconListItem v-if="handelse.orsak" label="Händelseorsak">
                            {{ handelse.orsak }}
                        </IconListItem>
                        <IconListItem v-if="handelse.beskrivning" label="Beskrivning händelseorsak">
                            {{ handelse.beskrivning }}
                        </IconListItem>
                    </IconList>
                    <IconList>
                        <IconListItem v-if="handelse.utforare?.fornamn" label="Utförare namn">
                            {{ `${handelse.utforare.fornamn} ${handelse.utforare.efternamn ?? ""}` }}
                        </IconListItem>
                        <IconListItem label="Utförare roll">{{ handelse.utforare?.roll }}</IconListItem>
                        <IconListItem v-if="handelse.utforare?.telefonnummer?.length" label="Utförare telefon">
                            <IconList>
                                <IconListItem
                                    v-for="telefonnummer in handelse.utforare.telefonnummer"
                                    :key="telefonnummer.value"
                                >
                                    {{ `${telefonnummer.value}${telefonnummer.available_to_patient ? "" : ejDelges}` }}
                                </IconListItem>
                            </IconList>
                        </IconListItem>
                        <IconListItem
                            v-if="!handelse.utforare?.fornamn && handelse.utforare?.fullstandigt_namn"
                            label="Utförare fullständigt namn"
                        >
                            {{ handelse.utforare.fullstandigt_namn }}
                        </IconListItem>
                        <IconListItem label="Utförare enhet">{{ handelse.utforare?.vardenhet?.namn }}</IconListItem>
                        <IconListItem
                            v-if="handelse.utforare?.vardenhet?.telefonnummer?.length"
                            label="Utförare enhet telefon"
                        >
                            <IconList>
                                <IconListItem
                                    v-for="telefonnummer in handelse.utforare.vardenhet.telefonnummer"
                                    :key="telefonnummer.value"
                                >
                                    {{ `${telefonnummer.value}${telefonnummer.available_to_patient ? "" : ejDelges}` }}
                                </IconListItem>
                            </IconList>
                        </IconListItem>
                        <IconListItem v-if="handelse.utforare?.vardenhet?.adress?.length" label="Utförare adress">
                            {{ handelse.utforare.vardenhet.adress.join(", ") }}
                        </IconListItem>
                    </IconList>
                </div>
                <div v-if="handelse.uppdragsgivare" class="separator">
                    <div class="line" />
                </div>
                <div v-if="handelse.uppdragsgivare" class="right">
                    <IconList>
                        <IconListItem v-if="handelse.uppdragsgivare?.fornamn" label="Uppdragsgivare namn">
                            {{ `${handelse.uppdragsgivare.fornamn} ${handelse.uppdragsgivare.efternamn ?? ""}` }}
                        </IconListItem>
                        <IconListItem v-if="handelse.uppdragsgivare?.roll" label="Uppdragsgivare roll">{{
                            handelse.uppdragsgivare.roll
                        }}</IconListItem>
                        <IconListItem
                            v-if="handelse.uppdragsgivare?.telefonnummer?.length"
                            label="Uppdragsgivare telefon"
                        >
                            <IconList>
                                <IconListItem
                                    v-for="telefonnummer in handelse.uppdragsgivare.telefonnummer"
                                    :key="telefonnummer.value"
                                >
                                    {{ `${telefonnummer.value}${telefonnummer.available_to_patient ? "" : ejDelges}` }}
                                </IconListItem>
                            </IconList>
                        </IconListItem>
                        <IconListItem
                            v-if="!handelse.uppdragsgivare?.fornamn && handelse.uppdragsgivare?.fullstandigt_namn"
                            label="Uppdragsgivare fullständigt namn"
                        >
                            {{ handelse.uppdragsgivare.fullstandigt_namn }}
                        </IconListItem>
                        <IconListItem v-if="handelse.uppdragsgivare?.vardenhet?.namn" label="Uppdragsgivare enhet">{{
                            handelse.uppdragsgivare.vardenhet.namn
                        }}</IconListItem>
                        <IconListItem
                            v-if="handelse.uppdragsgivare?.vardenhet?.telefonnummer?.length"
                            label="Uppdragsgivare enhet telefon"
                        >
                            <IconList>
                                <IconListItem
                                    v-for="telefonnummer in handelse.uppdragsgivare.vardenhet.telefonnummer"
                                    :key="telefonnummer.value"
                                >
                                    {{ `${telefonnummer.value}${telefonnummer.available_to_patient ? "" : ejDelges}` }}
                                </IconListItem>
                            </IconList>
                        </IconListItem>
                        <IconListItem
                            v-if="handelse.uppdragsgivare?.vardenhet?.adress?.length"
                            label="Uppdragsgivare adress"
                        >
                            {{ handelse.uppdragsgivare.vardenhet.adress.join(", ") }}
                        </IconListItem>
                    </IconList>
                </div>
            </div>
        </KSection>
    </div>
</template>

<style lang="scss" scoped>
    @import "@/style/variables";
    @import "@/style/container";

    .recept-tab-handelser {
        display: flex;
        flex-direction: column;
        width: $content-max-width;
        max-width: 100%;

        .heading {
            display: flex;

            .handelse {
                width: 12rem;
            }
        }

        .section-content {
            display: flex;
            flex: 1;
            gap: 1.5rem;
            width: 100%;
            max-width: $content-max-width;

            .left,
            .right {
                display: grid;
                grid-auto-flow: row;
                grid-auto-rows: max-content;
                grid-template-columns: repeat(2, auto);
                grid-row-gap: 1.5rem;
                flex: 1;
                max-width: max-content;

                ::v-deep .icon-list {
                    display: grid;
                    grid-column: 1 / -1;
                    grid-template-columns: subgrid;
                }

                ::v-deep .icon-list-item {
                    grid-auto-flow: row;

                    .content {
                        width: 100%;
                    }
                }
            }

            .separator {
                .line {
                    border: 1px solid $divider-color;
                    height: 100%;
                }
            }
        }

        &.variant-simple {
            font-size: 0.9rem;
        }
    }
</style>
